import type { EntityState } from '@core/ngrx-legacy';
import { createEntityCollectionStore, getEntityCollectionActions } from '@core/ngrx-legacy';
import { InitialDataChecksumToken } from '@core/reports-types';

import type {
  FetchOneReportResultsFailedPayload,
  FetchOneReportResultsPayload,
  FetchOneReportResultsSuccessPayload,
  ReportResultsEntity,
} from './results.types';
import { safeReportsStateSelector } from '../helpers/safe-selector';

const selectDataId = (data: { paramsHash: string }) => data.paramsHash;

const initialData = (): ReportResultsEntity => ({
  paramsHash: InitialDataChecksumToken,
  response: {
    checksum: InitialDataChecksumToken,
    meta: {},
    results: {
      columns: [],
      rows: [],
    },
  },
});

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportResultsEntity>()('ReportsResults', {
    idSelector: selectDataId,
    baseSelector: (root): EntityState<ReportResultsEntity> =>
      safeReportsStateSelector(root, namespace, initialState),

    customActions: adapter => {
      const {
        createOneOptimistic,
        updateOneOptimistic,
        createOneOptimisticSuccess,
        createOneOptimisticFailed,
      } = getEntityCollectionActions(adapter, selectDataId);

      return {
        fetchOneResult: (state, { paramsHash }: FetchOneReportResultsPayload) =>
          state.entityMap[paramsHash]
            ? updateOneOptimistic(state, {
                correlationId: paramsHash,
                data: state.entityMap[paramsHash]!,
              })
            : createOneOptimistic(state, {
                correlationId: paramsHash,
                data: initialData(),
              }),
        fetchOneResultSuccess: (
          state,
          { response, paramsHash }: FetchOneReportResultsSuccessPayload,
        ) =>
          createOneOptimisticSuccess(state, {
            correlationId: paramsHash,
            data: { response, paramsHash },
          }),

        fetchOneResultFailed: (state, { paramsHash, error }: FetchOneReportResultsFailedPayload) =>
          createOneOptimisticFailed(state, {
            correlationId: paramsHash,
            data: <any>{
              paramsHash: paramsHash,
            },
            error: <any>error,
          }),
      };
    },
  });

const selectResultById =
  (id: string) =>
  (state: EntityState<ReportResultsEntity>): ReportResultsEntity =>
    selectors.byId(id)(state) ?? initialData();

export const ReportsResultsSelectors = {
  byId: selectResultById,
  meta: selectors.meta,
};

export {
  actions as ReportsResultsActions,
  reducer as ReportsResultsReducer,
  namespace as ReportsResultsNamespace,
};
